import { Button, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React from 'react';

import { deleteReseller, getResellers, IReseller } from '~/src/dash/api/console-api';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  RouteResellerExisting,
  RouteResellerNew,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { deleteColor } from '~/src/dash/utils/material-dashboard-react';
import { routeName } from '~/src/dash/utils/routes';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  resellers: IReseller[];

  count: number;
  loading: boolean;
  updating: boolean;

  deleteModelActive: boolean;
  deleteModelID: string;
  deleteModelName: string;

  snackbarMessage: string;
  snackbarError: boolean;
}

export const ResellerListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      textField: {
        margin: 0,
        minWidth: '400px',
        width: '30%',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      resellers: [] as IReseller[],

      count: 0,
      loading: false,
      updating: false,

      deleteModelActive: false,
      deleteModelID: '',
      deleteModelName: '',

      snackbarMessage: '',
      snackbarError: false,
    };

    componentDidMount() {
      this.refreshModels();
    }

    _refreshModels = (count: number) => {
      if (count !== this.state.count) {
        return;
      }
      getResellers().then(
        (resellers: IReseller[]) => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            resellers,
            loading: false,
          });
        },
        () => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            resellers: [],
          });
        },
      );
    };

    refreshModels = () => {
      const count = this.state.count + 1;
      this.setState(
        {
          loading: true,
          count,
        },
        () => {
          this._refreshModels(count);
        },
      );
    };

    deleteResellerSettings = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      const model = this.findReseller(currentTarget.value);
      if (model) {
        this.setState({
          deleteModelActive: true,
          deleteModelID: model.ID,
          deleteModelName: model.Name,
        });
      } else {
        this.setState({
          snackbarMessage: `Failed to update header bidder`,
          snackbarError: true,
        });
      }
    };

    findReseller = (id: string) => {
      return this.state.resellers.reduce((ret: IReseller | undefined, r: IReseller) => {
        return r.ID === id ? r : ret;
      }, undefined);
    };

    viewResellerSettings = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      navigate(RouteResellerExisting, extractRouteParams(this.props), {
        re: currentTarget.value,
      });
    };

    viewResellerSettingsByID = (id: string) => {
      this.viewResellerSettings({ currentTarget: { value: id } } as unknown as any);
    };

    createReseller = () => {
      navigate(RouteResellerNew, extractRouteParams(this.props));
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    onHideDeleteDialog = () => {
      this.setState({
        deleteModelActive: false,
      });
    };

    onDeleteDeleteDialog = () => {
      this.onHideDeleteDialog();
      this.refreshModels();
    };

    renderRows() {
      if (this.state.loading) {
        return (
          <TableRow>
            <TableCell colSpan={4}>Loading...</TableCell>
          </TableRow>
        );
      }
      if (this.state.resellers.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={4}>No Data available</TableCell>
          </TableRow>
        );
      }
      return this.state.resellers.map((row: IReseller) => {
        return (
          <TableRow key={'r_' + row.ID}>
            <TableCell align='left'>
              <Button onClick={this.viewResellerSettings} value={row.ID}>
                <div style={{ textAlign: 'left' }}>
                  <strong>{row.Name}</strong>
                </div>
              </Button>
            </TableCell>
            <TableCell align='center'>{'logo holder'}</TableCell>
            <TableCell align='center'>
              <Button onClick={this.deleteResellerSettings} value={row.ID}>
                <DeleteIcon style={{ fill: deleteColor }} />
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largeHorizontalPadding verticalPadding>
            <div style={{ paddingTop: '25px', paddingBottom: '40px', paddingRight: '60px' }}>
              <RowButton
                right={
                  <ModelMediumButton
                    variant='contained'
                    color='primary'
                    style={{ paddingLeft: '30px', paddingRight: '30px' }}
                    onClick={this.createReseller}
                  >
                    + Create New
                  </ModelMediumButton>
                }
              />
            </div>
            <Paper classes={{ root: classes.paper }}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>Reseller Name</TableCell>
                    <TableCell align='center'>Reseller Logo</TableCell>
                    <TableCell align='center'>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{this.renderRows()}</TableBody>
              </Table>
            </Paper>
            <ModelDialogDeleteView
              active={this.state.deleteModelActive}
              model='Reseller'
              ID={this.state.deleteModelID}
              Name={this.state.deleteModelName}
              deleteModel={(reseller_id: string) => {
                return deleteReseller({
                  query: {
                    publisher_id: this.props.session.publisher.ID,
                    reseller_id,
                  },
                });
              }}
              onDelete={this.onDeleteDeleteDialog}
              onError={this.onHideDeleteDialog}
              onClose={this.onHideDeleteDialog}
            />
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
